import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useMutation } = ReactQueryV5;
import { Switch, Tooltip, useSnackbar } from "@mindlercare/ui-web";
import { useMindlerApi } from "api/apiv2";
import {
  useAllowedToViewPatientIcbt,
  useIcbtCommunicationFeatureEnabledForPatient,
  useInvalidateIcbtClaims,
} from "api/useIcbtQueries";
import { logger } from "core/logger";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import PatientItem from "./patientItem";

// Matches height & width of SwitchBoxToggle from @mindlercare/ui-web
const LoadingSkeleton = styled.div`
  width: 34px;
  height: 14px;
  background-color: #e0e0e0;
  border-radius: 15px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

// Matches the height of the SwitchBoxToggle from @mindlercare/ui-web to align the
// toggle switch correctly
const AlignmentWrapper = styled.div`
  display: inline-block;
  vertical-align: middle;
  height: 14px; /* Match the SwitchBoxToggle height from @mindlercare/ui-web */
`;

// Hook to handle state sync between external server(s) and switch toggle
const useIcbtCommunicationFeatureStatus = (patientUserId: number) => {
  const [hasInitialized, setHasInitialized] = useState(false);
  const [icbtCommunicationSwitchIsOn, setIcbtCommunicationSwitchIsOn] =
    useState(false);

  // icbt communication status from icbt claims via indianapolis
  const {
    data: {
      hasIcbtResponseFeatureEnabled,
      hasIcbtResponseFeatureEnabledInExternalSystem,
    },
    isFetching,
    error,
  } = useIcbtCommunicationFeatureEnabledForPatient(patientUserId);

  // Sync local state with external data once it's loaded
  useEffect(() => {
    if (isFetching || hasInitialized) return;
    // If data was fetched, but is not defined (some error case or not data, default to
    // false)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIcbtCommunicationSwitchIsOn(hasIcbtResponseFeatureEnabled ?? false);

    setHasInitialized(true);
  }, [isFetching, hasIcbtResponseFeatureEnabled, hasInitialized]);
  return {
    icbtCommunicationSwitchIsOn,
    setIcbtCommunicationSwitchIsOn,
    isFetching,
    // default to false if data not available
    isEnabledExternally: hasIcbtResponseFeatureEnabledInExternalSystem ?? false,
    error,
  };
};

const ToggleSwitchWrapper = ({
  tooltipContent,
  children,
}: {
  tooltipContent: string;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  return (
    <PatientItem title={t("ICBT_FEEDBACK_TITLE")}>
      <Tooltip content={tooltipContent}>
        <AlignmentWrapper>{children}</AlignmentWrapper>
      </Tooltip>
    </PatientItem>
  );
};

export const PatientToggleIcbtCommunication = ({
  patientUserId,
}: {
  patientUserId: number;
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const invalidateIcbtClaims = useInvalidateIcbtClaims();
  const mindlerApi = useMindlerApi();

  const {
    mutateAsync: activatePatientUserSetting,
    isPending: isActivationPending,
  } = useMutation(
    mindlerApi.patients.userSettings.addOrActivate.mutationOptions(),
  );

  const {
    mutateAsync: deactivatePatientUserSetting,
    isPending: isDeactivationPending,
  } = useMutation(
    mindlerApi.patients.userSettings.deactivate.mutationOptions(),
  );

  const isTogglePending = isActivationPending || isDeactivationPending;

  const isUserAuthorizedToToggle = useAllowedToViewPatientIcbt(patientUserId);

  const {
    icbtCommunicationSwitchIsOn,
    setIcbtCommunicationSwitchIsOn,
    isFetching,
    isEnabledExternally,
    error,
  } = useIcbtCommunicationFeatureStatus(patientUserId);
  const disabledToggle = isEnabledExternally || !isUserAuthorizedToToggle;

  const tooltipContent = isFetching
    ? t("LOADING")
    : isEnabledExternally
      ? t("ICBT_FEEDBACK_TOGGLE_DISABLED_EXTERNALLY")
      : isUserAuthorizedToToggle
        ? t("ICBT_FEEDBACK_ALLOWED_TO_TOGGLE")
        : t("ICBT_FEEDBACK_TOGGLE_DISABLED_UNAUTHORIZED");

  const toggleIcbtCommunication = async (enable: boolean) => {
    // optimistic UI
    setIcbtCommunicationSwitchIsOn(enable);
    const mutate = enable
      ? activatePatientUserSetting
      : deactivatePatientUserSetting;

    await mutate(
      { patientUserId, setting: "RECEIVE_ICBT_FEEDBACK_DATE" },
      {
        onSuccess: () => {
          enqueueSnackbar({
            text: enable
              ? t("ICBT_FEEDBACK_ENABLED")
              : t("ICBT_FEEDBACK_DISABLED"),
            isError: false,
          });
          // Refetch claims after update
          invalidateIcbtClaims();
        },
        onError: () => {
          setIcbtCommunicationSwitchIsOn(!enable); // rollback optimistic UI
          enqueueSnackbar({
            text: t("UNCAUGHT_FAILURE"),
            isError: true,
          });
        },
      },
    );
  };

  const handleToggle = () =>
    toggleIcbtCommunication(!icbtCommunicationSwitchIsOn);

  if (error) {
    logger.error("Error fetching ICBT communication feature status", {
      error,
      patientUserId,
    });
    return (
      <PatientItem
        title={t("ICBT_FEEDBACK_TITLE")}
        value={t("FAILED_FETCH")}
        valueTextColor="RED"
      />
    );
  }

  return (
    <ToggleSwitchWrapper tooltipContent={tooltipContent}>
      {isFetching ? (
        <LoadingSkeleton />
      ) : (
        <Switch
          isLeft
          disabled={disabledToggle || isTogglePending}
          isActive={icbtCommunicationSwitchIsOn}
          onClick={handleToggle}
        />
      )}
    </ToggleSwitchWrapper>
  );
};
